import { useState, useEffect } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { buttonStyles } from "utils/buttonStyles";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";

// Material Dashboard 2 React examples
import DataTable from "../../../examples/Tables/DataTable";

// Data
import data from "./ProjectsNotisSummary";
import { apiGetBahagianList } from "api/dashboard";

function Projects() {
  const { showSuccess, showError } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  const { columns, rows, isKEWUser, selectedBahagian, setSelectedBahagian, fetchTunggakanData } = data();
  const [menu, setMenu] = useState(null);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [bahagianOptions, setBahagianOptions] = useState([]);

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  // Fetch bahagian list on mount (only for KEW users)
  useEffect(() => {
    if (isKEWUser) {
      (async () => {
        const bahagians = await apiGetBahagianList();
        setBahagianOptions(bahagians);
      })();
    }
  }, [isKEWUser]);

  const handleBahagianChange = (event) => {
    const newBahagian = event.target.value;
    setSelectedBahagian(newBahagian);
    fetchTunggakanData(newBahagian);
  };

  const handleRebuildAllLejar = async () => {
    try {
      const confirmed = await showConfirm({
        title: 'Rebuild Semua Lejar',
        message: 'Anda pasti mahu Rebuild Lejar untuk semua akaun? Proses ini mungkin mengambil masa.',
        confirmText: 'Rebuild',
        type: 'warning'
      });
      if (!confirmed) return;
      setIsRebuilding(true);
      const { fetchWithCsrfRetry } = await import("api/csrf");
      const resp = await fetchWithCsrfRetry(`/api/lejar/rebuild-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ onlyAktif: true }),
      });
      if (!resp.ok) throw new Error("Gagal rebuild lejar secara pukal.");
      const result = await resp.json();
      const msg = `Rebuild siap: ${result.processed}/${result.total} akaun.` +
        (result.errors && result.errors.length ? ` Ralat: ${result.errors.length}` : "");
      window.appNotify && window.appNotify({ color: 'success', icon: 'check_circle', title: 'Berjaya', content: msg });
    } catch (err) {
      console.error("Bulk rebuild lejar failed:", err);
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal rebuild lejar semua akaun.' });
    } finally {
      setIsRebuilding(false);
    }
  };

  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <MDBox>
          <MDTypography variant="h6" gutterBottom>
            {isKEWUser ? "Tunggakan" : "Ringkasan Notis Tunggakan"}
          </MDTypography>
          {!isKEWUser && (
            <MDBox display="flex" alignItems="center" lineHeight={0}>
              <Icon
                sx={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: 20,
                  verticalAlign: "middle",
                }}
              >
                warning
              </Icon>
              <MDTypography
                variant="button"
                fontWeight="regular"
                ml={0.5}
                sx={{
                  color: "red",
                  fontWeight: "bold",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.7 },
                    "100%": { opacity: 1 },
                  },
                }}
              >
                PERHATIAN!
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
        {isKEWUser && bahagianOptions.length > 0 && (
          <MDBox display="flex" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedBahagian}
                onChange={handleBahagianChange}
                sx={{
                  height: 32,
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    py: 0.5,
                  }
                }}
              >
                {bahagianOptions.map((bahagian) => (
                  <MenuItem key={bahagian} value={bahagian}>
                    {bahagian}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MDBox>
        )}
      </MDBox>
    <MDBox>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
          fontSize={12}
        />
      </MDBox>
    </Card>
  );
}

export default Projects;
